<template>
	<div class="profile-image-selector-wrapper">
		<div class="uploader-input-container">
			<!-- Uploader Input -->
			<input
			hidden
			ref="fileUpload"
			class="input-file"
			type="file"
			accept=".png, .jpeg, .jpg"
			@change="onFileChange"/>
			<div class="image-wrapper">
				<!-- Image placeholder -->
				<img v-show="!imageIsLoaded" class="default-image" :src="imagePlaceholder"/>
				<!-- User image -->
				<img v-show="imageIsLoaded" class="selected-image" :src="ourImage" @error="imageErrorHandler" @load="imageLoadHandler"/>
			</div>
		</div>
		<div class="uploader-buttons-container">
			<div class="buttons-section">
				<button-secondary
				v-if="!imageIsLoaded"
				class="upload-image-button"
				icon="add-photo"
				buttonSize="small"
				:label="t('uploader.select')"
				@clicked="chooseFiles"/>
				<button-secondary
				v-if="imageIsLoaded"
				icon="cycle"
				buttonSize="small"
				:label="t('uploader.replace')"
				@clicked="chooseFiles"/>
				<button-negative
				v-if="imageIsLoaded"
				class="trash-button"
				icon="delete"
				buttonSize="small"
				buttonVariant="only-border"
				@clicked="showDeleteModal = true"/>
			</div>
			<div class="informative-section">
				<p class="types">{{t('uploader.typeImageMessage')}}</p>
				<p class="sizes">{{t('uploader.sizeImageMessage')}}</p>
			</div>
		</div>
		<!-- Upload Modal -->
		<upload-modal-web
		v-if="showUploadModal && !invalidFileExtension"
		:userImageData="userImageData"
		:ourImage="ourImage"
		@fileChange="chooseFiles"
		@saveImage="saveImageChangeHandler"
		@hideMe="showUploadModal = false"/>
		<!-- Delete Image Modal -->
		<delete-image-modal
		v-if="showDeleteModal"
		@rightButtonClicked="handleDelete"
		@hideMe="showDeleteModal = false"/>
	</div>
	<!-- Feedback -->
    <toast-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
	<!-- Loader -->
	<loader-generic v-if="showOverlaySpinner" isOverlay/>
</template>

<script setup>
import ButtonSecondary from '@/components/generic/buttons/Secondary'
import ButtonNegative from '@/components/generic/buttons/Negative'
import ToastGeneric from '@/components/generic/toasts/Generic'
import LoaderGeneric from '@/components/generic/loaders/Generic'
import UploadModalWeb from '@/components/generic/uploader/UploadModalWeb'
import DeleteImageModal from '@/components/generic/uploader/profileImageUploader/DeleteImageModal'

/* Vue */
import { ref, computed, watch } from 'vue'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { cancelAllPendingRequests } from '@/store/helpers/accessControl'
import { requestCustomerImageUrl } from '@/network/api/customers/customers.api.js'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const emit = defineEmits(['hasProfilePicture'])

/* mapActions & mapGetters */
const { updateCustomerImage, deleteCustomerImage } = mapActions(moduleMapper.USER_AREA_PROFILE)
const { updateCustomerImageUrl } = mapActions(moduleMapper.SESSION)
const { getAllCustomerInfo } = mapGetters(moduleMapper.SESSION)
const { getAppThemeMode } = mapGetters()

// template file input reference
const fileUpload = ref(null)

const ourImage = ref(getAllCustomerInfo.value.customer?.avatarImageUrl)
const triedFetchingImg = ref(false)

const maxFilesize = ref(4)

const showOverlaySpinner = ref(false)

const imageIsLoaded = ref(false)

const showUploadModal = ref(false)

const showDeleteModal = ref(false)

const userImageData = ref({
	imageUrl: '',
	type: '',
	imageName: '',
	imageBinaryBlob: null
})

const invalidFileExtension = ref(false)

const imagePlaceholder = computed(() => {
    return getAppThemeMode.value === 'dark' ? './images/user_image_placeholder_dark.svg' : './images/user_image_placeholder.svg'
})

/** triggers the file input and opens the window to choose files
@return { void }
*/
const chooseFiles = () => {
	showFeedbackToast.value = false
    fileUpload.value.click()
}

/** saves the target files and creates image
@event { HTMLEvent } - event information
@return { void }
*/
const onFileChange = (file) => {
    const files = file.target.files || file.dataTransfer.files

	if (file && (files[0].size > maxFilesize.value * 1024 * 1024)) {
		feedbackToastData.value = {
			toastType: 'error',
			text: t('uploader.errorOversized'),
			buttons: []
		}

		file.target.value = '' // We need to reset target.value to allow the same image to be selected

		showFeedbackToast.value = true
		return
	}

	showUploadModal.value = true

    createImage(files[0])

	file.target.value = '' // We need to reset target.value to allow the same image to be selected
}

const convertDataURIToBinary = dataURI => {
	var BASE64_MARKER = ';base64,'
	var base64Index = dataURI.indexOf(BASE64_MARKER) + BASE64_MARKER.length
	var base64 = dataURI.substring(base64Index)
	var raw = window.atob(base64)
	var rawLength = raw.length
	var array = new Uint8Array(new ArrayBuffer(rawLength))
	for (var i = 0; i < rawLength; i++) {
		array[i] = raw.charCodeAt(i)
	}
	return array
}

/** creates image blob and propagates it
@file { object } - event information
@return { void }
*/
const createImage = (file) => {
    const reader = new FileReader()

    reader.onload = event => {
		var base64Img = event.target.result // gets the image in base 64
		var binaryImg = convertDataURIToBinary(base64Img) // converts base 64 to binary
		var blob = new Blob([binaryImg], { type: file.type }) // creates the blob with the binary
		var fileName = file.name // get the name of tile

		userImageData.value.imageUrl = event.target.result
		userImageData.value.imageName = fileName
		userImageData.value.imageBinaryBlob = blob
    }

    reader.readAsDataURL(file)
}

/** validates if image is loaded
@return { void }
*/
const imageLoadHandler = () => {
    imageIsLoaded.value = true
}

/** hangles image load error
@return { void }
*/
const imageErrorHandler = () => {
	console.log(ourImage.value, 'imageLoadError')
    if (!triedFetchingImg.value) {
        triedFetchingImg.value = true
        if (process.env.VUE_APP_ENV === 'QUA') {
            ourImage.value = requestCustomerImageUrl()
            return
        }
    }
	imageIsLoaded.value = false
}

/* Controls if the file extension is valid or invalid and shows error toast if invalid */
watch(() => userImageData.value.imageBinaryBlob, () => {
	const type = userImageData.value.imageBinaryBlob.type

	invalidFileExtension.value = type !== 'image/jpeg' && type !== 'image/jpg' && type !== 'image/png'

	if (invalidFileExtension.value) {
		showFeedbackToast.value = true
		feedbackToastData.value = {
			toastType: 'negative',
			text: t('uploader.errorToast.invalidFileFormatText')
		}
	}
})

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const saveImageChangeHandler = async () => {
    showOverlaySpinner.value = true

	const formData = new FormData()
	formData.append('file', userImageData.value.imageBinaryBlob, userImageData.value.imageName)
	console.log(formData, 'formData')
    try {
        const response = await updateCustomerImage(formData)
        triedFetchingImg.value = false
		ourImage.value = response.data.data.avatarImageUrl

		updateCustomerImageUrl(response.data.data.avatarImageUrl)
        successHandler(response.data)
		showUploadModal.value = false

		emit('hasProfilePicture', true)
	} catch (error) {
		if (error.toString() === 'Cancel') return
		errorHandler(error.response ? error.response.data : error, saveImageChangeHandler)
	}
}

const handleDelete = async () => {
	cancelAllPendingRequests()
	showDeleteModal.value = false
	showOverlaySpinner.value = true
	try {
        const res = await deleteCustomerImage()
		ourImage.value = ''
		updateCustomerImageUrl('')
        successHandler(res.data)

		emit('hasProfilePicture', false)
	} catch (error) {
		if (error.toString() === 'Cancel') return
		errorHandler(error)
	}
}

/*************************************************
*                                                *
*                 Toast Handler                  *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref({
	toastType: '',
	text: '',
	buttons: []
})

const successHandler = response => {
	feedbackToastData.value = {
		toastType: 'success',
		text: response.message,
		buttons: []
	}

	showFeedbackToast.value = true
	showOverlaySpinner.value = false
}

const errorHandler = (data, retryFn) => {
	feedbackToastData.value = {
        toastType: data.errorLevel ? data.errorLevel.toLowerCase() : 'error',
		text: data && data.message ? data.message : t('genericErrorMessage'),
		buttons: [
            {
				text: t('uploader.errorToast.ignore'),
				callbackAction: () => { showFeedbackToast.value = false }
			},
			{
                text: t('uploader.errorToast.tryAgain'),
				callbackAction: retryFn
			}
		]
	}

	showFeedbackToast.value = true
	showOverlaySpinner.value = false
}
</script>

<style lang="scss" scoped>
.profile-image-selector-wrapper {
	width: 100%;
	height: 170px;

	position: relative;
	display: flex;

	.uploader-input-container {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: center;

		.image-wrapper {
			width: 120px;
			height: 120px;

			margin-right: 16px;
			border-radius: 50%;

			overflow: hidden;

			.selected-image, .default-image {
				width: 120px;
				height: 120px;
				border-radius: 50%;

				object-fit: cover;
			}
		}
	}

	.uploader-buttons-container {
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;

		.buttons-section {
			max-width: 200px;
			display: flex;
			flex-direction: row;
			justify-content: center;
			align-items: center;

			margin-bottom: 16px;
			gap: 8px;

			.upload-image-button {
				min-width: 125px;
			}
		}

		.informative-section {
			display: flex;
			flex-direction: column;
			justify-content: flex-start;
			align-items: flex-start;
			align-self: flex-start;

			gap: 2px;
			pointer-events: none;

			.types, .sizes {
				@include H9-Regular;
				color: var(--secondary-dark-2);
			}
		}
	}

	:deep(.overlay-modal-wrapper) {
		width: auto;

		.modal-header {
			margin-bottom: 0;
		}
	}
}
</style>
